// Client API du module « Facture » (/api/factures), brique 5B.
//
// Une facture naît en BROUILLON (sans numéro), se modifie/supprime librement, puis est ÉMISE
// (numéro FAC_AAAA-NNN attribué, document figé et INALTÉRABLE). Les transitions de statut
// passent par des routes dédiées (emettre / statut), pas par le PATCH de brouillon.

import { api } from './api'
import type { FactureStatut, FactureTransition } from '@shared/schemas/facture'
import type { ClientType } from '@shared/schemas/client'
import type { DevisDetail } from './devis'
import type { DepotDetail } from './depots'

export type { FactureStatut, FactureTransition }
export {
  FACTURE_STATUTS,
  LIBELLES_STATUT_FACTURE,
  calculerTotaux,
  UNITES_LIGNE,
  LIBELLES_UNITE_LIGNE,
  ABREV_UNITE_LIGNE,
  abrevUniteLigne,
} from '@shared/schemas/facture'
export type { UniteLigne } from '@shared/schemas/facture'

export interface LigneFacture {
  id: string
  articleId: string | null
  designation: string
  quantite: number
  prixUnitaire: number
  unite: string | null
  ordre: number | null
}

export interface FactureListItem {
  id: string
  numero: string | null
  clientNom: string | null
  statut: FactureStatut
  dateFacture: string
  dateEcheance: string | null
  tvaPct: number
  totalHt: number
  createdAt: string
}

// Coordonnées du client jointes pour l'en-tête du PDF (null si pas de client / supprimé).
export interface FactureClientDetail {
  type: ClientType
  adresse: string | null
  codePostal: string | null
  ville: string | null
  pays: string
  email: string | null
  telephone: string | null
  numeroTva: string | null
  siret: string | null
}

export interface FactureDetail extends FactureListItem {
  clientId: string | null
  remise: number
  fraisPort: number
  mentionTva: string | null
  emiseLe: string | null
  notes: string | null
  updatedAt: string
  client: FactureClientDetail | null
  lignes: LigneFacture[]
}

export interface LigneFacturePayload {
  articleId?: string | null
  designation: string
  quantite: number
  prixUnitaire: number
  unite?: string | null
  ordre?: number | null
}

// Création / mise à jour d'un BROUILLON (jamais de `statut` ici : émission via route dédiée).
export interface FacturePayload {
  clientId?: string | null
  dateFacture: string
  dateEcheance?: string | null
  remise: number
  fraisPort: number
  tvaPct?: number | null
  notes?: string | null
  lignes: LigneFacturePayload[]
}

export async function fetchFactures(): Promise<FactureListItem[]> {
  const { factures } = await api<{ factures: FactureListItem[] }>('/api/factures')
  return factures
}

export async function fetchUneFacture(id: string): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}`)
  return facture
}

export async function creerFacture(payload: FacturePayload): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>('/api/factures', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return facture
}

export async function majFacture(id: string, payload: Partial<FacturePayload>): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return facture
}

export async function supprimerFacture(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/factures/${id}`, { method: 'DELETE' })
}

// Émet la facture (brouillon -> émise) : numéro attribué, mention TVA figée, sortie de stock 'vente'.
export async function emettreFacture(id: string): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}/emettre`, {
    method: 'POST',
  })
  return facture
}

// Change le statut d'une facture émise : 'payee' ou 'annulee'.
export async function changerStatutFacture(id: string, statut: FactureTransition): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}/statut`, {
    method: 'PATCH',
    body: JSON.stringify({ statut }),
  })
  return facture
}

// Télécharge le PDF Factur-X (PDF/A-3b + XML CII embarqué) d'une facture ÉMISE. On n'utilise PAS
// le helper `api` (qui attend du JSON) : la réponse est un binaire. Le cookie de session part
// automatiquement (credentials: 'include'). En cas d'échec, on relaie le message d'erreur serveur.
export async function telechargerFacturX(id: string, nomFichier: string): Promise<void> {
  const reponse = await fetch(`/api/factures/${id}/facturx`, { credentials: 'include' })
  if (!reponse.ok) {
    let message = 'Échec de la génération du Factur-X.'
    try {
      const corps = (await reponse.json()) as { error?: string }
      if (corps?.error) message = corps.error
    } catch {
      /* réponse non-JSON : on garde le message générique */
    }
    throw new Error(message)
  }
  const blob = await reponse.blob()
  const url = URL.createObjectURL(blob)
  const lien = document.createElement('a')
  lien.href = url
  lien.download = nomFichier
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  URL.revokeObjectURL(url)
}

// Actions disponibles sur une facture selon son statut (source unique pour les boutons de la liste).
// Reflète exactement les règles du service : seul un brouillon se modifie/supprime/émet ; une facture
// émise se paie ou s'annule ; une facture payée s'annule ; une facture annulée ne fait plus que
// s'imprimer. L'impression est possible dès qu'un numéro existe (statut ≠ brouillon).
export interface ActionsFacture {
  peutModifier: boolean
  peutSupprimer: boolean
  peutEmettre: boolean
  peutPayer: boolean
  peutAnnuler: boolean
  peutImprimer: boolean
}

export function actionsPourStatut(statut: FactureStatut): ActionsFacture {
  return {
    peutModifier: statut === 'brouillon',
    peutSupprimer: statut === 'brouillon',
    peutEmettre: statut === 'brouillon',
    peutPayer: statut === 'emise',
    peutAnnuler: statut === 'emise' || statut === 'payee',
    peutImprimer: statut !== 'brouillon',
  }
}

// Construit le payload d'un BROUILLON de facture à partir d'un devis (décision a : « convertir
// un devis en facture »). Recopie client + lignes + remise + frais de port + notes ; le devis
// reste inchangé. La date de facture est fournie par l'appelant (le jour de la conversion) —
// fonction PURE, donc pas d'horloge à l'intérieur (testable). On ne fixe pas `tvaPct` : le
// serveur applique le défaut du régime de la cliente (0 en franchise, taux standard au réel).
export function payloadFactureDepuisDevis(devis: DevisDetail, dateFacture: string): FacturePayload {
  return {
    clientId: devis.clientId,
    dateFacture,
    remise: devis.remise,
    fraisPort: devis.fraisPort,
    notes: devis.notes,
    lignes: devis.lignes.map((l, i) => ({
      articleId: l.articleId,
      designation: l.designation,
      quantite: l.quantite,
      prixUnitaire: l.prixUnitaire,
      unite: l.unite,
      ordre: l.ordre ?? i,
    })),
  }
}

// Construit le payload d'un BROUILLON de facture à partir d'un dépôt-vente RÉGULARISÉ
// (brique D4). Le document facture le DÉPOSITAIRE (la boutique) du montant net qui revient à la
// savonnière : `reversement` = total vendu − commission. Le DÉTAIL des produits vendus est
// repris ligne par ligne (quantité vendue × prix), et la commission dépositaire est portée en
// REMISE GLOBALE (un pourcentage = tauxCommission) : le calcul de facture applique les mêmes
// formules d'arrondi que le calcul du dépôt, donc le total HT retombe EXACTEMENT sur
// `reversement`. Toutes les lignes sont LIBRES (articleId null) : le stock a déjà été sorti à
// l'ACTIVATION du dépôt (motif 'depot', brique D3), donc AUCUN mouvement de stock à l'émission.
// Repli : si aucune vente régularisée, on retombe sur une ligne unique au montant net (le front
// bloque déjà la facturation d'un reversement nul). Fonction PURE (date fournie par l'appelant).
// On ne fixe pas `tvaPct` : le serveur applique le régime du compte (0 en franchise 293 B).
export function payloadFactureDepuisDepot(depot: DepotDetail, dateFacture: string): FacturePayload {
  const ref = depot.numero ? ` ${depot.numero}` : ''
  // Une ligne par produit RÉELLEMENT vendu (quantité vendue > 0), au prix plein : la somme des
  // lignes = totalVendu, sur lequel la remise globale applique la commission.
  const lignesVendues: LigneFacturePayload[] = depot.lignes
    .filter((l) => l.quantiteVendue > 0)
    .map((l, i) => ({
      articleId: null, // ligne libre -> aucune sortie de stock à l'émission (déjà sortie en D3)
      designation: l.designation,
      quantite: l.quantiteVendue,
      prixUnitaire: l.prixUnitaire,
      unite: null,
      ordre: l.ordre ?? i,
    }))

  const detaille = lignesVendues.length > 0
  return {
    clientId: depot.clientId,
    dateFacture,
    // La commission dépositaire est une réfaction globale : remise = tauxCommission (%).
    // (Repli sans vente : pas de remise, la ligne unique porte déjà le montant net.)
    remise: detaille ? depot.tauxCommission : 0,
    fraisPort: 0,
    notes: `Reversement dépôt-vente${ref}`,
    lignes: detaille
      ? lignesVendues
      : [
          {
            articleId: null,
            designation: `Reversement dépôt-vente${ref}`,
            quantite: 1,
            prixUnitaire: depot.reversement,
            unite: null,
            ordre: 0,
          },
        ],
  }
}
